import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { getArticleFaq, type ArticleFaqItem } from "@/lib/articleFaq.functions";
import { useLocale, useT } from "@/i18n/useLocale";

interface ArticleFaqProps {
  articleId: string;
  onItems?: (items: ArticleFaqItem[]) => void;
}

const ArticleFaq = ({ articleId, onItems }: ArticleFaqProps) => {
  const fetchFaq = useServerFn(getArticleFaq);
  const locale = useLocale();
  const t = useT();
  const [items, setItems] = useState<ArticleFaqItem[]>([]);
  const requestedFor = useRef<string | null>(null);
  const currentId = useRef<string | null>(null);

  useEffect(() => {
    currentId.current = articleId;
    const requestKey = `${articleId}:${locale}`;
    if (!articleId || requestedFor.current === requestKey) return;
    requestedFor.current = requestKey;
    setItems([]);

    (async () => {
      try {
        const result = await fetchFaq({ data: { articleId, languageCode: locale } });
        if (currentId.current !== articleId) return;
        if (!result?.success || !result.items?.length) return;
        setItems(result.items);
        onItems?.(result.items);
      } catch {
        // A missing FAQ is not an error the reader needs to see.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, locale]);

  if (items.length === 0) return null;

  return (
    <section
      aria-labelledby="article-faq-heading"
      className="border-t border-border pt-8 mt-12"
    >
      <h2
        id="article-faq-heading"
        className="text-2xl font-display font-semibold mb-6"
      >
        {t("article.faqTitle")}
      </h2>
      <div className="space-y-6">
        {items.map((item) => (
          <div key={item.question}>
            <h3 className="text-lg font-semibold mb-2 leading-snug">{item.question}</h3>
            <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ArticleFaq;
